import { Ribbon } from 'icons';
import { useTranslation } from 'react-i18next';

import { Tag, TagColor, Tooltip } from '@cfa/react-components';

import { formatDate, formatDateAsUtc } from '@/utils/date';
import { getDueDateColor, getDueDateString } from '@/utils/dueDate';
import { cn } from '@/utils/tailwind';

interface BadgeProps {
  isComplete: boolean;
  completionDate?: string | null;
  selectedDueDate: number;
  showDate: boolean;
  isMyPlan?: boolean;
}

const Badge: React.FC<BadgeProps> = ({
  isComplete,
  completionDate,
  selectedDueDate,
  showDate,
  isMyPlan = false,
}) => {
  const { t } = useTranslation();

  const timeLeft = getDueDateString(selectedDueDate);
  const timeLeftColor = getDueDateColor(selectedDueDate) as TagColor;

  if (isComplete) {
    return (
      <Tooltip
        content={
          <p className="text-variant-body2 text-grayscale-gray7 font-normal">
            {t('TrainingPlans.statusOptions.completed')}{' '}
            {completionDate ? formatDate(completionDate) : t('Generic.na')}
          </p>
        }
        placement="top"
        showOnElementEvents={['hover']}
      >
        <Ribbon className={cn('size-8!')} />
      </Tooltip>
    );
  }

  if (showDate && selectedDueDate && isMyPlan) {
    return (
      <Tooltip
        content={t('TrainingPlans.accountability.due.dueDate', {
          dueDate: formatDateAsUtc(selectedDueDate),
        })}
        placement="top"
        showOnElementEvents={['hover']}
      >
        <div>
          <Tag
            className="whitespace-nowrap!"
            color={timeLeftColor}
            data-testid="DateTag"
            label={timeLeft}
            variant="filled"
          />
        </div>
      </Tooltip>
    );
  }

  if (showDate && selectedDueDate && !isNaN(selectedDueDate)) {
    return (
      <Tooltip
        content={t('TrainingPlans.accountability.due.dueDate', {
          dueDate: selectedDueDate
            ? formatDateAsUtc(selectedDueDate)
            : t('TrainingPlans.accountability.due.noDueDate'),
        })}
        placement="top"
        showOnElementEvents={['hover']}
      >
        <div>
          <Tag
            className="whitespace-nowrap!"
            color={timeLeftColor}
            data-testid="DateTag"
            label={timeLeft}
            variant="filled"
          />
        </div>
      </Tooltip>
    );
  }

  return null;
};

export default Badge;
